import {
  Bot,
  Briefcase,
  Building2,
  CalendarDays,
  CirclePlay,
  Clock,
  FileText,
  Home,
  Library,
  Palette,
  Plug,
  ReceiptText,
  Rocket,
  Sparkles,
  Users,
  Zap,
  type LucideIcon
} from "lucide-react";
import type { FC } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

import type { AppTab } from "../types";

/** The sidebar's icon for each page tab. */
export const PAGE_ICONS: Record<string, LucideIcon> = {
  home: Home,
  calendar: CalendarDays,
  cases: Briefcase,
  clients: Users,
  companies: Building2,
  time: Clock,
  "billing-review": ReceiptText,
  "firm-knowledge": Library,
  automations: Zap,
  connectors: Plug,
  tutorials: CirclePlay,
  agents: Bot,
  athena: Sparkles,
  "design-system": Palette,
  onboarding: Rocket
};

export const pageIcon = (pageId: string | undefined): LucideIcon =>
  (pageId && PAGE_ICONS[pageId]) || FileText;

/**
 * A tab's leading mark: the page's lucide icon, or a case's or client's
 * rounded-square avatar as a neutral monogram. `tile` draws page
 * icons on a muted tile (avatar density and the overflow stack).
 */
export const TabMark: FC<{
  tab: Pick<AppTab, "kind" | "pageId" | "initials" | "avatarUrl" | "title">;
  size?: "sm" | "md" | "lg";
  tile?: boolean;
  className?: string;
}> = ({ tab, size = "sm", tile, className }) => {
  const box = size === "lg" ? "size-6" : size === "md" ? "size-5" : "size-4";
  if (tab.kind === "page") {
    const Icon = pageIcon(tab.pageId);
    return tile ? (
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-xs bg-fill-2 text-fg-3",
          box,
          className
        )}
      >
        <Icon className="size-3" />
      </span>
    ) : (
      <Icon className={cn("size-3.5 shrink-0", className)} />
    );
  }
  const initials = tab.initials || tab.title.slice(0, 2).toUpperCase();
  return (
    <Avatar className={cn("shrink-0 rounded-xs", box, className)}>
      {tab.avatarUrl ? <AvatarImage src={tab.avatarUrl} alt="" /> : null}
      <AvatarFallback
        className={cn("text-2xs font-medium", size === "lg" && "text-xs")}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};
